import pick from 'lodash/pick';
import { initiatePrivileged, transitionPrivileged } from '../../util/api';
import { denormalisedResponseEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import * as log from '../../util/log';
import { fetchCurrentUserHasOrdersSuccess, fetchCurrentUser } from '../../ducks/user.duck';
import transactions from '../../api/transactions';
import defaultConfig from '../../config/configDefault';
import { isPrivileged, transitions } from '../../transactions/transactionProcessBooking';
import { isSameDay } from '../../util/dates';
import { getPartnerDataForEntites } from '../../util/partner';

// ================ Action types ================ //

export const SET_INITIAL_VALUES = 'app/CheckoutPage/SET_INITIAL_VALUES';

export const INITIATE_ORDER_REQUEST = 'app/CheckoutPage/INITIATE_ORDER_REQUEST';
export const INITIATE_ORDER_SUCCESS = 'app/CheckoutPage/INITIATE_ORDER_SUCCESS';
export const INITIATE_ORDER_ERROR = 'app/CheckoutPage/INITIATE_ORDER_ERROR';

export const CONFIRM_PAYMENT_REQUEST = 'app/CheckoutPage/CONFIRM_PAYMENT_REQUEST';
export const CONFIRM_PAYMENT_SUCCESS = 'app/CheckoutPage/CONFIRM_PAYMENT_SUCCESS';
export const CONFIRM_PAYMENT_ERROR = 'app/CheckoutPage/CONFIRM_PAYMENT_ERROR';

export const SPECULATE_TRANSACTION_REQUEST = 'app/CheckoutPage/SPECULATE_TRANSACTION_REQUEST';
export const SPECULATE_TRANSACTION_SUCCESS = 'app/CheckoutPage/SPECULATE_TRANSACTION_SUCCESS';
export const SPECULATE_TRANSACTION_ERROR = 'app/CheckoutPage/SPECULATE_TRANSACTION_ERROR';

export const STRIPE_CUSTOMER_REQUEST = 'app/CheckoutPage/STRIPE_CUSTOMER_REQUEST';
export const STRIPE_CUSTOMER_SUCCESS = 'app/CheckoutPage/STRIPE_CUSTOMER_SUCCESS';
export const STRIPE_CUSTOMER_ERROR = 'app/CheckoutPage/STRIPE_CUSTOMER_ERROR';

// ================ Reducer ================ //

const initialState = {
  listing: null,
  bookingData: null,
  bookingDates: null,
  transaction: null,
  speculateTransactionInProgress: false,
  speculateTransactionError: null,
  speculatedTransaction: null,
  initiateOrderError: null,
  confirmPaymentError: null,
  stripeCustomerFetched: false,
};

export default function checkoutPageReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case SET_INITIAL_VALUES:
      return { ...initialState, ...payload };

    case SPECULATE_TRANSACTION_REQUEST:
      return {
        ...state,
        speculateTransactionInProgress: true,
        speculateTransactionError: null,
        speculatedTransaction: null,
      };
    case SPECULATE_TRANSACTION_SUCCESS:
      return {
        ...state,
        speculateTransactionInProgress: false,
        speculatedTransaction: payload.transaction,
      };
    case SPECULATE_TRANSACTION_ERROR:
      console.error(payload); // eslint-disable-line no-console
      return {
        ...state,
        speculateTransactionInProgress: false,
        speculateTransactionError: payload,
      };

    case INITIATE_ORDER_REQUEST:
      return { ...state, initiateOrderError: null };
    case INITIATE_ORDER_SUCCESS:
      return { ...state, transaction: payload };
    case INITIATE_ORDER_ERROR:
      console.error(payload); // eslint-disable-line no-console
      return { ...state, initiateOrderError: payload };

    case CONFIRM_PAYMENT_REQUEST:
      return { ...state, confirmPaymentError: null };
    case CONFIRM_PAYMENT_SUCCESS:
      return state;
    case CONFIRM_PAYMENT_ERROR:
      console.error(payload); // eslint-disable-line no-console
      return { ...state, confirmPaymentError: payload };

    case STRIPE_CUSTOMER_REQUEST:
      return { ...state, stripeCustomerFetched: false };
    case STRIPE_CUSTOMER_SUCCESS:
      return { ...state, stripeCustomerFetched: true };
    case STRIPE_CUSTOMER_ERROR:
      console.error(payload); // eslint-disable-line no-console
      return { ...state, stripeCustomerFetchError: payload };

    default:
      return state;
  }
}

// ================ Action creators ================ //

export const setInitialValues = initialValues => ({
  type: SET_INITIAL_VALUES,
  payload: pick(initialValues, Object.keys(initialState)),
});

const initiateOrderRequest = () => ({ type: INITIATE_ORDER_REQUEST });
const initiateOrderSuccess = order => ({ type: INITIATE_ORDER_SUCCESS, payload: order });
const initiateOrderError = e => ({
  type: INITIATE_ORDER_ERROR,
  error: true,
  payload: e,
});

const confirmPaymentRequest = () => ({ type: CONFIRM_PAYMENT_REQUEST });
const confirmPaymentSuccess = orderId => ({ type: CONFIRM_PAYMENT_SUCCESS, payload: orderId });
const confirmPaymentError = e => ({
  type: CONFIRM_PAYMENT_ERROR,
  error: true,
  payload: e,
});

const speculateTransactionRequest = () => ({ type: SPECULATE_TRANSACTION_REQUEST });
const speculateTransactionSuccess = transaction => ({
  type: SPECULATE_TRANSACTION_SUCCESS,
  payload: { transaction },
});
const speculateTransactionError = e => ({
  type: SPECULATE_TRANSACTION_ERROR,
  error: true,
  payload: e,
});

const stripeCustomerRequest = () => ({ type: STRIPE_CUSTOMER_REQUEST });
const stripeCustomerSuccess = () => ({ type: STRIPE_CUSTOMER_SUCCESS });
const stripeCustomerError = e => ({
  type: STRIPE_CUSTOMER_ERROR,
  error: true,
  payload: e,
});

/* ================ Thunks ================ */

export const initiateOrder = (orderParams, transactionId) => (dispatch, getState, sdk) => {
  dispatch(initiateOrderRequest());

  // If we already have a transaction ID, we should transition, not initiate.
  const isTransition = !!transactionId;
  
  // Accept custom transition from orderParams (for zero-payment vouchers)
  // This allows CheckoutPage to specify REQUEST_PAYMENT_DISCOUNT_FULL transition
  const transition = orderParams.transition || (isTransition
    ? transitions.REQUEST_PAYMENT_AFTER_ENQUIRY
    : transitions.REQUEST_PAYMENT);
  const isPrivilegedTransition = isPrivileged(transition);

  const {
    startDate,
    endDate,
    seats,
    sessions,
    adults,
    children,
    ...restOfOrderParams
  } = orderParams;
  const bookingData = {
    startDate,
    endDate,
    seats,
    sessions,
    adults,
    children,
    ...restOfOrderParams,
  };

  const params = {
    ...restOfOrderParams,
    bookingStart: startDate,
    bookingEnd: endDate,
    seats,
    adults,
    children,
  };

  const isSameDayBooking = startDate && endDate && isSameDay(startDate, endDate);
  if (params.protectedData) {
    params.protectedData.isSameDayBooking = isSameDayBooking;
  } else {
    params.protectedData = { isSameDayBooking };
  }

  const partnerFromState = getState().ui.partner;
  if (partnerFromState) {
    const partner = getPartnerDataForEntites(partnerFromState);

    if (params.protectedData) {
      params.protectedData.partner = partner;
    } else {
      params.protectedData = { partner };
    }
  }

  const bodyParams = isTransition
    ? { id: transactionId, transition, params }
    : { processAlias: defaultConfig.bookingProcessAlias, transition, params };

  const queryParams = { include: ['booking', 'provider'], expand: true };

  const handleSucces = response => {
    const entities = denormalisedResponseEntities(response);
    const order = entities[0];
    dispatch(initiateOrderSuccess(order));
    dispatch(fetchCurrentUserHasOrdersSuccess(true));
    return order;
  };

  const handleError = e => {
    console.error('[INITIATE ORDER ERROR] ❌ Failed:', {
      error: e,
      errorStatus: e.status,
      errorMessage: e.message,
      errorData: e.data,
      transactionId: transactionId?.uuid,
      hadVoucher: !!orderParams.voucher,
      voucher: orderParams.voucher
    });
    dispatch(initiateOrderError(storableError(e)));
    const transactionIdMaybe = transactionId ? { transactionId: transactionId.uuid } : {};
    log.error(e, 'initiate-order-failed', {
      ...transactionIdMaybe,
      listingId: orderParams.listingId.uuid,
      bookingStart: orderParams.bookingStart,
      bookingEnd: orderParams.bookingEnd,
    });
    throw e;
  };

  if (isTransition && isPrivilegedTransition) {
    // transition privileged
    return transitionPrivileged({ isSpeculative: false, bookingData, bodyParams, queryParams })
      .then(handleSucces)
      .catch(handleError);
  } else if (isTransition) {
    // transition non-privileged
    return sdk.transactions
      .transition(bodyParams, queryParams)
      .then(handleSucces)
      .catch(handleError);
  } else if (isPrivilegedTransition) {
    // initiate privileged
    return initiatePrivileged({ isSpeculative: false, bookingData, bodyParams, queryParams })
      .then(handleSucces)
      .catch(handleError);
  } else {
    // initiate non-privileged
    return sdk.transactions
      .initiate(bodyParams, queryParams)
      .then(handleSucces)
      .catch(handleError);
  }
};

export const confirmPayment = orderParams => (dispatch, getState, sdk) => {
  dispatch(confirmPaymentRequest());

  const { transactionId, isWithoutStripe } = orderParams;

  // ZERO-PAYMENT FLOW: When voucher covers 100% (payin = 0), transaction goes directly to preauthorized
  // via request-payment-discount-full transition. No confirm-payment needed.
  // Check if this is zero-payment case (isWithoutStripe flag from Step 2)
  if (isWithoutStripe) {
    // Fetch current transaction state to confirm it's preauthorized
    return sdk.transactions
      .show({ id: transactionId, include: ['booking', 'provider', 'customer'] })
      .then(response => {
        const order = response.data.data;
        const txState = order.attributes.lastTransition;
        if (txState === 'transition/request-payment-discount-full') {
          // Transaction is already in preauthorized state, treat as success
          dispatch(confirmPaymentSuccess(order.id));
          return order;
        } else {
          // Unexpected state, throw error
          throw new Error(`Unexpected transaction state for zero-payment: ${txState}`);
        }
      });
  }

  // NORMAL PAYMENT FLOW: Stripe payment required
  const transition = transitions.CONFIRM_PAYMENT;

  const bodyParams = {
    id: transactionId,
    transition,
    params: {},
  };

  return sdk.transactions
    .transition(bodyParams)
    .then(response => {
      const order = response.data.data;
      dispatch(confirmPaymentSuccess(order.id));

      transactions.transitionTransaction(order.id.uuid, transition).catch(() => {});

      return order;
    })
    .catch(async e => {
      dispatch(confirmPaymentError(storableError(e)));
      const transactionIdMaybe = transactionId ? { transactionId: transactionId.uuid } : {};
      log.error(e, 'initiate-order-failed', {
        ...transactionIdMaybe,
      });
      
      // Revert voucher immediately when payment confirmation fails
      // Transaction stays in pending-payment state and voucher was already recorded
      if (transactionId) {
        try {
          // Fetch transaction to check if it has a voucher
          const txResponse = await sdk.transactions.show({ id: transactionId });
          const transaction = txResponse.data.data;
          const voucherCode = transaction?.attributes?.protectedData?.voucherCode;
          
          if (voucherCode) {
            // Call revert API
            const apiBaseUrl = typeof window !== 'undefined' ? window.location.origin : '';
            await fetch(`${apiBaseUrl}/api/revert-voucher`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              credentials: 'include',
              body: JSON.stringify({
                transactionId: transactionId.uuid || transactionId,
                transition: 'confirm-payment-failed',
              }),
            });
          }
        } catch (revertError) {
          // Don't block error flow if revert fails
          console.error('[VOUCHER] Failed to revert after payment failure:', revertError);
        }
      }
      
      throw e;
    });
};

export const sendMessage = params => (dispatch, getState, sdk) => {
  const message = params.message;
  const orderId = params.id;

  if (message) {
    return sdk.messages
      .send({ transactionId: orderId, content: message })
      .then(() => {
        return { orderId, messageSuccess: true };
      })
      .catch(e => {
        log.error(e, 'initial-message-send-failed', { txId: orderId });
        return { orderId, messageSuccess: false };
      });
  } else {
    return Promise.resolve({ orderId, messageSuccess: true });
  }
};

/**
 * Initiate or transition the speculative transaction with the given
 * booking details
 *
 * IMPORTANT: This function supports TWO modes:
 * 1. isSpeculative=true (DEFAULT): Calculate pricing without creating real transaction
 *    - Used for checkout page preview
 *    - No transaction created in Sharetribe
 *    - No voucher usage recorded
 * 
 * 2. isSpeculative=false: Create real transaction when user clicks "Confirm request"
 *    - Used when user initiates payment
 *    - Real transaction created
 *    - Voucher usage recorded
 *
 * @param {Object} orderParams - Booking details (dates, seats, voucher, etc.)
 * @param {String} transactionId - Existing transaction ID (for transitions)
 * @param {Boolean} isSpeculative - true = preview only, false = real transaction (default: true)
 */
export const speculateTransaction = (orderParams, transactionId, isSpeculative = true) => (dispatch, getState, sdk) => {
  dispatch(speculateTransactionRequest());

  // If we already have a transaction ID, we should transition, not
  // initiate.
  const isTransition = !!transactionId;
  const transition = isTransition
    ? transitions.REQUEST_PAYMENT_AFTER_ENQUIRY
    : transitions.REQUEST_PAYMENT;
  const isPrivilegedTransition = isPrivileged(transition);

  const {
    startDate,
    endDate,
    seats,
    sessions,
    adults,
    children,
    ...restOfOrderParams
  } = orderParams;
  const bookingData = { 
    startDate, 
    endDate, 
    seats, 
    sessions, 
    adults, 
    children,
    ...restOfOrderParams  // Include voucher and any other params
  };
  
  const params = {
    bookingStart: startDate,
    bookingEnd: endDate,
    seats,
    ...restOfOrderParams,
    cardToken: 'CheckoutPage_speculative_card_token',
  };

  const bodyParams = isTransition
    ? { id: transactionId, transition, params }
    : { processAlias: defaultConfig.bookingProcessAlias, transition, params };

  const queryParams = { include: ['booking', 'provider'], expand: true };

  const handleSuccess = response => {
    const entities = denormalisedResponseEntities(response);
    if (entities.length !== 1) {
      throw new Error('Expected a resource in the speculate response');
    }
    const tx = entities[0];
    dispatch(speculateTransactionSuccess(tx));
  };

  const handleError = e => {
    const { listingId, bookingStart, bookingEnd } = params;
    log.error(e, 'speculate-transaction-failed', {
      listingId: listingId.uuid,
      bookingStart,
      bookingEnd,
    });
    return dispatch(speculateTransactionError(storableError(e)));
  };

  if (isTransition && isPrivilegedTransition) {
    // transition privileged
    return transitionPrivileged({ isSpeculative, bookingData, bodyParams, queryParams })
      .then(handleSuccess)
      .catch(handleError);
  } else if (isTransition) {
    // transition non-privileged
    return sdk.transactions
      .transitionSpeculative(bodyParams, queryParams)
      .then(handleSuccess)
      .catch(handleError);
  } else if (isPrivilegedTransition) {
    // initiate privileged
    // ✅ Pass isSpeculative flag from function parameter (defaults to true)
    return initiatePrivileged({ isSpeculative, bookingData, bodyParams, queryParams })
      .then(handleSuccess)
      .catch(handleError);
  } else {
    // initiate non-privileged
    return sdk.transactions
      .initiateSpeculative(bodyParams, queryParams)
      .then(handleSuccess)
      .catch(handleError);
  }
};

// StripeCustomer is a relantionship to currentUser
// We need to fetch currentUser with correct params to include relationship
export const fetchStripeCustomer = () => (dispatch, getState, sdk) => {
  dispatch(stripeCustomerRequest());

  return dispatch(fetchCurrentUser({ include: ['stripeCustomer.defaultPaymentMethod'] }))
    .then(() => dispatch(stripeCustomerSuccess()))
    .catch(e => dispatch(stripeCustomerError(storableError(e))));
};

export const loadData = () => (dispatch, getState) => {
  return dispatch(fetchStripeCustomer());
};
